/**
 * FL-67: 複数住所の自動判定機能
 *
 * OCRで抽出された複数の住所から、引き取り先・搬入先を自動判定するユーティリティ
 */

import { ExtractedAddress } from '../types';

/**
 * 住所の種類
 */
export enum AddressType {
  PICKUP = 'pickup', // 引き取り先
  DELIVERY = 'delivery', // 搬入先
  UNKNOWN = 'unknown', // 判定不可
}

/**
 * 住所判定結果
 */
export interface AddressDetectionResult {
  address: ExtractedAddress;
  type: AddressType;
  confidence: number; // 判定の信頼度（0-1）
  reason: string; // 判定理由
}

/**
 * 住所分類のキーワード辞書
 */
const ADDRESS_KEYWORDS = {
  pickup: [
    // 引き取り関連キーワード
    '引取',
    '引き取り',
    '回収',
    '集荷',
    '発送元',
    '差出人',
    '送り主',
    'ピックアップ',
    'pickup',
    '取りに',
    '受取',
    '納車場所',
    // 企業・営業所系
    '営業所',
    '支店',
    '店舗',
    '工場',
    '倉庫',
    '配送センター',
    '本社',
    '支社',
    '事業所',
    '営業部',
    '物流',
  ],
  delivery: [
    // 搬入関連キーワード
    '搬入',
    '納入',
    '配送先',
    '届け先',
    '宛先',
    '納車先',
    '配達先',
    'デリバリー',
    'delivery',
    '届ける',
    '搬送先',
    '設置先',
    // 住宅・個人系
    '様',
    'さん',
    '宅',
    '邸',
    '住宅',
    'マンション',
    'アパート',
    'ハイツ',
    '団地',
    'コーポ',
    'レジデンス',
    '一戸建',
    '戸建',
  ],
};

/**
 * 地域による判定（都市部は搬入先、郊外は引き取り先の可能性が高い）
 */
const URBAN_PREFECTURES = [
  '東京都',
  '神奈川県',
  '大阪府',
  '愛知県',
  '福岡県',
  '千葉県',
  '埼玉県',
  '兵庫県',
];

const URBAN_CITIES = [
  '新宿区',
  '渋谷区',
  '品川区',
  '港区',
  '中央区',
  '千代田区',
  '横浜市',
  '川崎市',
  '大阪市',
  '名古屋市',
  '福岡市',
  '仙台市',
  '札幌市',
  '広島市',
  '北九州市',
];

/**
 * 単一住所の種類を判定
 */
export const detectAddressType = (
  address: ExtractedAddress,
  context?: string
): AddressDetectionResult => {
  let pickupScore = 0;
  let deliveryScore = 0;
  const reasons: string[] = [];

  // テキスト（rawText + fullAddress）でキーワード検索
  const searchText = `${address.rawText} ${address.fullAddress} ${context || ''}`.toLowerCase();

  // 引き取り関連キーワードをチェック
  ADDRESS_KEYWORDS.pickup.forEach(keyword => {
    if (searchText.includes(keyword.toLowerCase())) {
      pickupScore += 1;
      reasons.push(`引き取りキーワード「${keyword}」検出`);
    }
  });

  // 搬入関連キーワードをチェック
  ADDRESS_KEYWORDS.delivery.forEach(keyword => {
    if (searchText.includes(keyword.toLowerCase())) {
      deliveryScore += 1;
      reasons.push(`搬入キーワード「${keyword}」検出`);
    }
  });

  // 建物名による判定
  if (address.buildingName) {
    if (
      address.buildingName.includes('営業所') ||
      address.buildingName.includes('支店') ||
      address.buildingName.includes('工場') ||
      address.buildingName.includes('倉庫')
    ) {
      pickupScore += 1.5;
      reasons.push('営業施設の建物名検出');
    } else if (address.buildingName.match(/(マンション|アパート|ハイツ|レジデンス)/)) {
      deliveryScore += 1.5;
      reasons.push('住宅系建物名検出');
    }
  }

  // 地域による推定
  if (address.prefecture && URBAN_PREFECTURES.includes(address.prefecture)) {
    deliveryScore += 0.5;
    reasons.push('都市部地域のため搬入先の可能性が高い');
  }

  if (address.city && URBAN_CITIES.some(city => address.city?.includes(city))) {
    deliveryScore += 0.5;
    reasons.push('主要都市のため搬入先の可能性が高い');
  }

  // 郵便番号による地域判定（簡易版）
  if (address.postalCode) {
    const postalPrefix = address.postalCode.substring(0, 3);
    // 東京都心部の郵便番号
    if (
      ['100', '101', '102', '103', '104', '105', '106', '107', '108', '150'].includes(postalPrefix)
    ) {
      deliveryScore += 0.3;
      reasons.push('都心部郵便番号検出');
    }
  }

  // 判定結果を決定
  let type: AddressType;
  let confidence: number;
  let reason: string;

  if (pickupScore > deliveryScore) {
    type = AddressType.PICKUP;
    confidence = Math.min(pickupScore / (pickupScore + deliveryScore + 1), 0.9);
    reason = `引き取り先と判定 (スコア: ${pickupScore.toFixed(1)} vs ${deliveryScore.toFixed(1)})`;
  } else if (deliveryScore > pickupScore) {
    type = AddressType.DELIVERY;
    confidence = Math.min(deliveryScore / (pickupScore + deliveryScore + 1), 0.9);
    reason = `搬入先と判定 (スコア: ${deliveryScore.toFixed(1)} vs ${pickupScore.toFixed(1)})`;
  } else {
    type = AddressType.UNKNOWN;
    confidence = 0.5;
    reason = '明確な判定ができませんでした';
  }

  // 理由の詳細を追加
  if (reasons.length > 0) {
    reason += ` - ${reasons.join(', ')}`;
  }

  return {
    address,
    type,
    confidence,
    reason,
  };
};

/**
 * 複数住所の自動分類
 */
export const detectMultipleAddressTypes = (
  addresses: ExtractedAddress[],
  context?: string
): AddressDetectionResult[] => {
  if (addresses.length === 0) return [];

  // 各住所を個別に判定
  const results = addresses.map(address => detectAddressType(address, context));

  // 複数住所がある場合の相対的な調整
  if (results.length >= 2) {
    // 最も引き取り先らしいものと搬入先らしいものを強化
    const pickupCandidates = results.filter(r => r.type === AddressType.PICKUP);
    const deliveryCandidates = results.filter(r => r.type === AddressType.DELIVERY);

    // 引き取り先候補がない場合、最も信頼度の低い搬入先を引き取り先に変更
    if (pickupCandidates.length === 0 && deliveryCandidates.length >= 2) {
      const leastConfidentDelivery = deliveryCandidates.reduce((min, current) =>
        current.confidence < min.confidence ? current : min
      );
      leastConfidentDelivery.type = AddressType.PICKUP;
      leastConfidentDelivery.confidence = 0.6;
      leastConfidentDelivery.reason = '相対判定により引き取り先に調整';
    }

    // 搬入先候補がない場合、最も信頼度の低い引き取り先を搬入先に変更
    if (deliveryCandidates.length === 0 && pickupCandidates.length >= 2) {
      const leastConfidentPickup = pickupCandidates.reduce((min, current) =>
        current.confidence < min.confidence ? current : min
      );
      leastConfidentPickup.type = AddressType.DELIVERY;
      leastConfidentPickup.confidence = 0.6;
      leastConfidentPickup.reason = '相対判定により搬入先に調整';
    }
  }

  // 信頼度でソート（高い順）
  return results.sort((a, b) => b.confidence - a.confidence);
};

/**
 * 住所タイプの表示名を取得
 */
export const getAddressTypeDisplayName = (type: AddressType): string => {
  switch (type) {
    case AddressType.PICKUP:
      return '引き取り先';
    case AddressType.DELIVERY:
      return '搬入先';
    case AddressType.UNKNOWN:
      return '判定不可';
    default:
      return '不明';
  }
};

/**
 * 住所タイプの色を取得（Tailwind CSS class）
 */
export const getAddressTypeColor = (type: AddressType): string => {
  switch (type) {
    case AddressType.PICKUP:
      return 'text-green-600 bg-green-50 border-green-200';
    case AddressType.DELIVERY:
      return 'text-blue-600 bg-blue-50 border-blue-200';
    case AddressType.UNKNOWN:
      return 'text-gray-600 bg-gray-50 border-gray-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};
